import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BrandingContext = createContext();

// Helper function to convert hex color to HSL format for Tailwind
const hexToHSL = (hex) => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  return `${h} ${s}% ${l}%`;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Default workflow stages - used only as fallback when DB config unavailable
const DEFAULT_WORKFLOW_STAGES = [
  {
    id: 'clay', name: 'Clay', order: 1,
    statuses: [
      { id: 'sculpting', name: 'In Progress' },
      { id: 'feedback_needed', name: 'Feedback Needed' },
      { id: 'changes_requested', name: 'Changes Requested' },
      { id: 'approved', name: 'Approved' },
    ]
  },
  {
    id: 'paint', name: 'Paint', order: 2,
    statuses: [
      { id: 'painting', name: 'In Progress' },
      { id: 'feedback_needed', name: 'Feedback Needed' },
      { id: 'changes_requested', name: 'Changes Requested' },
      { id: 'approved', name: 'Approved' },
    ]
  },
  {
    id: 'shipped', name: 'Shipped', order: 3,
    statuses: [
      { id: 'in_transit', name: 'In Transit' },
      { id: 'delivered', name: 'Delivered' },
    ]
  },
  {
    id: 'archived', name: 'Archived', order: 4,
    statuses: [
      { id: 'completed', name: 'Completed' },
      { id: 'canceled', name: 'Canceled' },
    ]
  }
];

const DEFAULT_BRANDING = {
  logo_url: '',
  primary_color: '#2196F3',
  secondary_color: '#9C27B0',
  font_family: 'Arial, sans-serif',
  font_size_base: '16px',
  company_name: ''
};

// Apply CSS variables immediately with whatever branding we have.
// Called both on first render (with defaults) and after the fetch resolves.
const applyCSSVariables = (branding) => {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', branding.primary_color);
  root.style.setProperty('--color-secondary', branding.secondary_color);
  root.style.setProperty('--primary', hexToHSL(branding.primary_color));
  root.style.setProperty('--secondary', hexToHSL(branding.secondary_color));
  root.style.setProperty('--font-family-base', branding.font_family);
  root.style.setProperty('--font-size-base', branding.font_size_base);
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [workflowConfig, setWorkflowConfig] = useState({
    stages: DEFAULT_WORKFLOW_STAGES,
    rules: [],
    timers: [],
    stage_labels: [],
    status_labels: []
  });
  const [loading, setLoading] = useState(true);

  // Apply default CSS variables immediately on first render so there is
  // never a flash of unstyled content — even before the fetch completes.
  useEffect(() => {
    applyCSSVariables(DEFAULT_BRANDING);
    fetchBrandingSettings();
  }, []);

  const fetchBrandingSettings = async () => {
    try {
      // ── FIX: use the public endpoint — no auth token required ──────────
      // The old code called /settings/tenant which requires VIEW_SETTINGS
      // permission. On a fresh session (no token yet) it bailed out early,
      // leaving the app with default colors until the user logged in AND
      // refreshed the page.
      //
      // The new /settings/public-branding endpoint returns only the safe
      // public fields (colors, fonts, logo, workflow config) with no auth.
      const response = await axios.get(`${API}/settings/public-branding`);
      const settings = response.data.settings || {};

      const newBranding = {
        logo_url:        settings.logo_url        || DEFAULT_BRANDING.logo_url,
        primary_color:   settings.primary_color   || DEFAULT_BRANDING.primary_color,
        secondary_color: settings.secondary_color || DEFAULT_BRANDING.secondary_color,
        font_family:     settings.font_family      || DEFAULT_BRANDING.font_family,
        font_size_base:  settings.font_size_base   || DEFAULT_BRANDING.font_size_base,
        company_name:    response.data.name        || DEFAULT_BRANDING.company_name,
      };

      setBranding(newBranding);

      // Apply CSS variables immediately when data arrives — no waiting for
      // the next render cycle, so colors snap in without a flash.
      applyCSSVariables(newBranding);

      // Load workflow config
      const dbWorkflowConfig = settings.workflow_config || {};
      const stages = dbWorkflowConfig.stages || DEFAULT_WORKFLOW_STAGES;
      const stageLabels = stages.map(s => s.name);
      const allStatuses = stages.flatMap(s => s.statuses || []);
      const uniqueStatusIds = [...new Set(allStatuses.map(st => st.id))];
      const statusLabels = uniqueStatusIds.map(id => {
        const status = allStatuses.find(st => st.id === id);
        return status?.name || id;
      });

      setWorkflowConfig({
        stages,
        rules:         dbWorkflowConfig.rules  || [],
        timers:        dbWorkflowConfig.timers || [],
        stage_labels:  stageLabels,
        status_labels: statusLabels,
      });
    } catch (error) {
      console.error('Failed to fetch branding settings:', error);
      // Apply defaults on error so CSS variables are always set
      applyCSSVariables(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  };

  // Keep CSS variables in sync whenever branding state changes
  // (e.g. after an admin saves new colors in Settings)
  useEffect(() => {
    applyCSSVariables(branding);
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, workflowConfig, loading, refreshBranding: fetchBrandingSettings }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext;